import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

import {
  InternalConversation,
  InternalConversationSnapshot,
  InternalConversationSyncCursor,
  InternalConversationSyncPage,
  InternalMessage,
} from '@/models/internalChat'
import { User } from '@/models/user'
import api from '@/shared/api'
import {
  ENDPOINTS,
  UTILS,
} from '@/shared/constants'
import { isVisibleInternalConversationForUser } from '@/shared/users'
import { RootState } from '..'

const internalConversationsAdapter = createEntityAdapter<InternalConversation>({
  sortComparer: (a, b) => {
    const aTime = a.lastMessage?.createdAt
      ? new Date(a.lastMessage?.createdAt).getTime()
      : null
    const bTime = b.lastMessage?.createdAt
      ? new Date(b.lastMessage?.createdAt).getTime()
      : null

    if (aTime !== null && bTime !== null) {
      return bTime - aTime
    }

    if (aTime === null && bTime !== null) {
      return 1
    }

    if (aTime !== null && bTime === null) {
      return -1
    }

    return a.title?.localeCompare(b.title)
  },
})

type InternalConversationsState = ReturnType<
  typeof internalConversationsAdapter.getInitialState
> & {
  isLoading: boolean
  isChatOpen: boolean
  isChangingOpenedConversation: boolean
  conversationsIds: { [conversaionId: string]: boolean }
  onlineUsers: { [userId: number]: boolean }
  unreadMessages: { [conversaionId: string]: number }
  loadedConversations: { [conversaionId: string]: string }
  totalUnread: number
  openedConversation?: InternalConversation
  openedConversationMessages: InternalMessage[]
  syncCursor?: InternalConversationSyncCursor
  availableSyncCursor?: InternalConversationSyncCursor
  syncRequestId?: string
  syncResetRequired: boolean
}

const initialState: InternalConversationsState = {
  ...internalConversationsAdapter.getInitialState(),
  isLoading: false,
  isChatOpen: false,
  isChangingOpenedConversation: false,
  conversationsIds: {},
  onlineUsers: {},
  unreadMessages: {},
  loadedConversations: {},
  totalUnread: 0,
  openedConversation: undefined,
  openedConversationMessages: [],
  syncCursor: undefined,
  availableSyncCursor: undefined,
  syncRequestId: undefined,
  syncResetRequired: false,
}

const internalConversationSyncPageSize = 200

function compareSequence(left: string, right: string): number {
  const normalizedLeft = left.replace(/^0+(?=\d)/, UTILS.BLANK)
  const normalizedRight = right.replace(/^0+(?=\d)/, UTILS.BLANK)

  if (normalizedLeft.length !== normalizedRight.length) {
    return normalizedLeft.length - normalizedRight.length
  }
  return normalizedLeft.localeCompare(normalizedRight)
}

function updateTotalUnread(unreadMessages: {
  [conversaionId: string]: number
}) {
  return Object.values(unreadMessages).reduce((total, count) => total + count, 0)
}

function updateMessage(messages: InternalMessage[], message: InternalMessage) {
  const index = messages.findIndex((m) => m.id === message.id)
  if (index !== -1) {
    messages[index] = { ...message }
  }
}

function shouldAddConversation(
  state: typeof initialState,
  conversation: InternalConversation,
  user: User
) {
  const exists = state.entities[conversation.id]
  const belongs = conversation.participants.some((p) => p.userId === user.id)
  return (
    !exists &&
    belongs &&
    isVisibleInternalConversationForUser(conversation, user)
  )
}

function addInternalConversationLogic(
  state: typeof initialState,
  conversation: InternalConversation,
  user: User
) {
  const validation = shouldAddConversation(state, conversation, user)
  if (validation) {
    internalConversationsAdapter.addOne(state, {
      ...conversation,
      messages: conversation.messages ?? [],
    })
    const unreadCount = conversation.unreadCount ?? 0
    if (unreadCount > 0) {
      state.unreadMessages[conversation.id] = unreadCount
      state.totalUnread = updateTotalUnread(state.unreadMessages)
    }
  }
}

export const fetchInternalConversations = createAsyncThunk<
  {
    data: InternalConversation[]
    user: User | null
    syncCursor?: string
    scope?: Pick<InternalConversationSyncCursor, 'companyId' | 'userId'>
  },
  {
    silent?: boolean
    reason?: 'initial' | 'socket_reconnect' | 'incremental_reset'
  } | undefined,
  { state: RootState }
>(
  'internalConversations/fetchInternalConversations',
  async (args, thunkAPI) => {
    try {
      const {
        auth: { user },
      } = thunkAPI.getState()
      const scope =
        user?.companyId != null
          ? { companyId: user.companyId, userId: user.id }
          : undefined
      const response = await api.get<InternalConversationSnapshot>(
        ENDPOINTS.INTERNAL_CONVERSATIONS.GET.SNAPSHOT_V2,
        {
          headers: {
            'x-internal-conversations-fetch-reason':
              args?.reason ?? 'initial',
          },
          signal: thunkAPI.signal,
        }
      )
      return {
        data: response.data.items,
        user,
        syncCursor: response.data.syncCursor,
        scope,
      }
    } catch (error: unknown) {
      if (!args?.silent) toast.error('Erro ao listar chat internos')
      let errorMessage = 'Unknown error'

      if (error instanceof Error) {
        errorMessage = error.message
      }

      return thunkAPI.rejectWithValue(errorMessage)
    }
  },
  {
    condition: (_, { getState }) => {
      return !getState().internalConversations.isLoading
    },
  }
)

interface SyncInternalConversationsResult {
  upserts: InternalConversation[]
  removedIds: string[]
  cursor: string
  resetRequired: boolean
  scope: Pick<InternalConversationSyncCursor, 'companyId' | 'userId'>
  fromCursor: string
}

export const syncInternalConversations = createAsyncThunk<
  SyncInternalConversationsResult,
  void,
  { state: RootState }
>(
  'internalConversations/syncInternalConversations',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState()
    const user = state.auth.user
    const syncCursor = state.internalConversations.syncCursor
    if (
      !user?.companyId ||
      !syncCursor ||
      syncCursor.companyId !== user.companyId ||
      syncCursor.userId !== user.id
    ) {
      throw new Error('Internal conversation sync cursor is not available')
    }

    const scope = { companyId: user.companyId, userId: user.id }
    const upserts = new Map<string, InternalConversation>()
    const removedIds = new Set<string>()
    const visitedCursors = new Set<string>([syncCursor.value])
    let cursor = syncCursor.value

    do {
      const response = await api.get<InternalConversationSyncPage>(
        ENDPOINTS.INTERNAL_CONVERSATIONS.GET.SYNC,
        {
          headers: {
            'x-internal-conversations-fetch-reason': 'incremental_sync',
          },
          params: { after: cursor, limit: internalConversationSyncPageSize },
          signal: thunkAPI.signal,
        }
      )
      const page = response.data
      if (page.resetRequired) {
        return {
          upserts: [],
          removedIds: [],
          cursor: syncCursor.value,
          resetRequired: true,
          scope,
          fromCursor: syncCursor.value,
        }
      }
      page.upserts.forEach((conversation) => {
        removedIds.delete(conversation.id)
        upserts.set(conversation.id, conversation)
      })
      page.removedIds.forEach((id) => {
        upserts.delete(id)
        removedIds.add(id)
      })

      if (!page.hasMore) {
        cursor = page.cursor
        break
      }
      if (page.cursor === cursor || visitedCursors.has(page.cursor)) {
        throw new Error('Invalid internal conversation sync pagination')
      }
      visitedCursors.add(page.cursor)
      cursor = page.cursor
    } while (!thunkAPI.signal.aborted)

    if (thunkAPI.signal.aborted) {
      throw new Error('Internal conversation sync aborted')
    }
    return {
      upserts: [...upserts.values()],
      removedIds: [...removedIds],
      cursor,
      resetRequired: false,
      scope,
      fromCursor: syncCursor.value,
    }
  },
  {
    condition: (_, { getState }) =>
      !getState().internalConversations.syncRequestId,
  }
)

export const reconcileInternalConversations = createAsyncThunk<
  void,
  void,
  { state: RootState }
>('internalConversations/reconcile', async (_, thunkAPI) => {
  const state = thunkAPI.getState()
  const user = state.auth.user
  const conversationsState = state.internalConversations
  if (!user?.companyId || conversationsState.isLoading) return

  const cursor = conversationsState.syncCursor
  const hasCurrentCursor =
    cursor?.companyId === user.companyId && cursor.userId === user.id
  if (!hasCurrentCursor) {
    await thunkAPI
      .dispatch(
        fetchInternalConversations({
          silent: true,
          reason: 'socket_reconnect',
        })
      )
      .unwrap()
    return
  }

  const result = await thunkAPI.dispatch(syncInternalConversations()).unwrap()
  if (result.resetRequired) {
    await thunkAPI
      .dispatch(
        fetchInternalConversations({
          silent: true,
          reason: 'incremental_reset',
        })
      )
      .unwrap()
  }
})

export const internalConversationsSlice = createSlice({
  name: 'internalConversations',
  initialState,
  reducers: {
    observeInternalConversationChangeSequence(
      state,
      action: PayloadAction<InternalConversationSyncCursor>
    ) {
      const current = state.availableSyncCursor
      if (
        !current ||
        current.companyId !== action.payload.companyId ||
        current.userId !== action.payload.userId ||
        compareSequence(action.payload.value, current.value) > 0
      ) {
        state.availableSyncCursor = action.payload
      }
    },
    setIsChatOpen: (state, action: PayloadAction<boolean>) => {
      state.isChatOpen = action.payload
    },
    initOnlineUsers: (
      state,
      action: PayloadAction<{ [key: number]: boolean }>
    ) => {
      state.onlineUsers = action.payload
    },
    setOnlineUser: (
      state,
      action: PayloadAction<{ userId: number; online: boolean }>
    ) => {
      state.onlineUsers[action.payload.userId] = action.payload.online
    },
    setIsChangingOpenedInternalConversation: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isChangingOpenedConversation = action.payload
    },
    setOpenedInternalConversation: (
      state,
      action: PayloadAction<InternalConversation | undefined>
    ) => {
      if (action.payload) {
        const conversation = {
          ...action.payload,
          messages: action.payload.messages ?? [],
        }
        state.openedConversation = conversation
        delete state.unreadMessages[action.payload.id]
        state.totalUnread = updateTotalUnread(state.unreadMessages)
        state.openedConversationMessages = conversation.messages
        internalConversationsAdapter.upsertOne(state, conversation)
      } else {
        state.openedConversation = undefined
        state.openedConversationMessages = []
      }
    },
    addInternalConversation: (
      state,
      action: PayloadAction<{ conversation: InternalConversation; user: User }>
    ) => {
      addInternalConversationLogic(
        state,
        action.payload.conversation,
        action.payload.user
      )
    },
    upsertInternalConversation: (
      state,
      action: PayloadAction<InternalConversation>
    ) => {
      const existing = internalConversationsAdapter
        .getSelectors()
        .selectById(state, action.payload.id)
      const conversation = {
        ...action.payload,
        messages: action.payload.messages ?? existing?.messages ?? [],
      }
      internalConversationsAdapter.upsertOne(state, conversation)
      state.conversationsIds[action.payload.id] = true
      if (state.openedConversation?.id === action.payload.id) {
        state.openedConversation = conversation
      }
    },
    removeInternalConversation: (state, action: PayloadAction<string>) => {
      internalConversationsAdapter.removeOne(state, action.payload)
      delete state.conversationsIds[action.payload]
      delete state.unreadMessages[action.payload]
      state.totalUnread = updateTotalUnread(state.unreadMessages)
      if (state.openedConversation?.id === action.payload) {
        state.openedConversation = undefined
        state.openedConversationMessages = []
      }
    },
    updateInternalConversation: (
      state,
      action: PayloadAction<{
        conversationId: string
        userId: number
        lastReadAt: string
      }>
    ) => {
      const existing = internalConversationsAdapter
        .getSelectors()
        .selectById(state, action.payload.conversationId)
      if (!existing) return

      const conversation = structuredClone(existing)

      const participantIndex = conversation.participants.findIndex(
        (p) => p.userId === action.payload.userId
      )
      if (participantIndex !== -1) {
        conversation.participants[participantIndex].lastReadAt =
          action.payload.lastReadAt
      }

      internalConversationsAdapter.updateOne(state, {
        id: conversation.id,
        changes: conversation,
      })

      if (state.openedConversation?.id === action.payload.conversationId) {
        state.openedConversation = conversation
      }
    },
    addMessageToInternalConversation: (
      state,
      action: PayloadAction<{ message: InternalMessage; user: User }>
    ) => {
      const existing = internalConversationsAdapter
        .getSelectors()
        .selectById(state, action.payload.message.internalConversationId)
      if (!existing) return

      const conversation = structuredClone(existing)

      const alreadyExists = conversation?.messages.some(
        (m) => m.id === action.payload.message.id
      )
      conversation.lastMessage = action.payload.message
      if (!alreadyExists) {
        if (conversation.messages) {
          conversation.messages.push(action.payload.message)
        } else {
          conversation.messages = [action.payload.message]
        }
      } else if (alreadyExists) {
        updateMessage(conversation.messages ?? [], action.payload.message)
      }

      // atualiza última mensagem e unreadCount se necessário
      if (
        state.openedConversation &&
        state.openedConversation.id === conversation.id
      ) {
        const alreadyExists = state.openedConversationMessages.some(
          (m) => m.id === action.payload.message.id
        )
        if (!alreadyExists) {
          state.openedConversationMessages.push(action.payload.message)
        } else if (alreadyExists) {
          updateMessage(
            state.openedConversationMessages,
            action.payload.message
          )
        }
      } else {
        if (action.payload.message.senderId !== action.payload.user.id) {
          state.unreadMessages[conversation.id] =
            (state.unreadMessages[conversation.id] ?? 0) + 1
          state.totalUnread = updateTotalUnread(state.unreadMessages)
        }
      }

      internalConversationsAdapter.updateOne(state, {
        id: conversation.id,
        changes: conversation,
      })
    },
    addInternalMessageToOpenedInternalConversationMessages(
      state,
      action: PayloadAction<InternalMessage>
    ) {
      const alreadyExists = state.openedConversationMessages.some(
        (m) => m.id === action.payload.id
      )
      if (!alreadyExists) {
        state.openedConversationMessages.push(action.payload)
      }
    },
    addInternalMessagesToOpenedInternalConversationMessages(
      state,
      action: PayloadAction<{
        conversationId: string
        messages: InternalMessage[]
      }>
    ) {
      if (state.openedConversation?.id === action.payload.conversationId) {
        state.openedConversationMessages = action.payload.messages.concat(
          state.openedConversationMessages
        )
      }
    },
    addLoadedInternalConversation(state, action: PayloadAction<string>) {
      state.loadedConversations[action.payload] = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInternalConversations.pending, (state) => {
        state.isLoading = true
      })
      .addCase(
        fetchInternalConversations.fulfilled,
        (state, action) => {
          state.isLoading = false
          const visibleConversations = action.payload.data.filter(
            (conversation) =>
              isVisibleInternalConversationForUser(
                conversation,
                action.payload.user
              )
          )

          internalConversationsAdapter.setAll(
            state,
            visibleConversations.map((incoming) => {
              const existing = state.entities[incoming.id]

              return {
                ...incoming,
                messages: existing?.messages
                  ? [...existing.messages] // mantém todas as mensagens já carregadas
                  : (incoming.messages ?? []),
              }
            })
          )
          state.totalUnread = 0
          state.unreadMessages = {}
          state.conversationsIds = {}
          visibleConversations.forEach((item) => {
            const { id, lastMessage } = item
            state.conversationsIds[id] = true
            if (lastMessage?.senderId !== action.payload.user?.id) {
              const lastReadAt = item.participants.find(
                (p) => p.userId === action.payload.user?.id
              )?.lastReadAt

              const hasUnread =
                lastMessage &&
                (!lastReadAt ||
                  new Date(lastMessage.createdAt) > new Date(lastReadAt))

              const unreadCount = item.unreadCount ?? (hasUnread ? 1 : 0)
              if (unreadCount > 0) {
                state.unreadMessages[id] = unreadCount
              }
              state.totalUnread = updateTotalUnread(state.unreadMessages)
            }
          })

          if (action.payload.syncCursor && action.payload.scope) {
            const nextCursor = {
              ...action.payload.scope,
              value: action.payload.syncCursor,
            }
            const scopeChanged =
              state.syncCursor?.companyId !== nextCursor.companyId ||
              state.syncCursor?.userId !== nextCursor.userId
            state.syncCursor = nextCursor
            state.syncResetRequired = false
            if (
              scopeChanged ||
              !state.availableSyncCursor ||
              compareSequence(
                nextCursor.value,
                state.availableSyncCursor.value
              ) > 0
            ) {
              state.availableSyncCursor = nextCursor
            }
          }
        }
      )
      .addCase(fetchInternalConversations.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(syncInternalConversations.pending, (state, action) => {
        state.syncRequestId = action.meta.requestId
      })
      .addCase(syncInternalConversations.fulfilled, (state, action) => {
        if (state.syncRequestId !== action.meta.requestId) return
        state.syncRequestId = undefined
        if (action.payload.resetRequired) {
          state.syncResetRequired = true
          return
        }
        if (
          state.syncCursor?.companyId !== action.payload.scope.companyId ||
          state.syncCursor?.userId !== action.payload.scope.userId ||
          state.syncCursor.value !== action.payload.fromCursor
        ) {
          return
        }

        const removedIds = new Set(action.payload.removedIds)
        internalConversationsAdapter.removeMany(
          state,
          action.payload.removedIds
        )
        action.payload.removedIds.forEach((id) => {
          delete state.conversationsIds[id]
          delete state.unreadMessages[id]
        })
        if (
          state.openedConversation &&
          removedIds.has(state.openedConversation.id)
        ) {
          state.openedConversation = undefined
          state.openedConversationMessages = []
        }

        action.payload.upserts.forEach((incoming) => {
          const existing = state.entities[incoming.id]
          const conversation = {
            ...incoming,
            messages: existing?.messages ?? incoming.messages ?? [],
          }
          internalConversationsAdapter.upsertOne(state, conversation)
          state.conversationsIds[incoming.id] = true
          const isOpened = state.openedConversation?.id === incoming.id
          if (!isOpened && incoming.unreadCount > 0) {
            state.unreadMessages[incoming.id] = incoming.unreadCount
          } else {
            delete state.unreadMessages[incoming.id]
          }
          if (isOpened) {
            state.openedConversation = {
              ...conversation,
              messages: state.openedConversationMessages,
            }
          }
        })
        state.totalUnread = updateTotalUnread(state.unreadMessages)

        const nextCursor = {
          ...action.payload.scope,
          value: action.payload.cursor,
        }
        state.syncCursor = nextCursor
        state.syncResetRequired = false
        if (
          !state.availableSyncCursor ||
          state.availableSyncCursor.companyId !== nextCursor.companyId ||
          state.availableSyncCursor.userId !== nextCursor.userId ||
          compareSequence(nextCursor.value, state.availableSyncCursor.value) > 0
        ) {
          state.availableSyncCursor = nextCursor
        }
      })
      .addCase(syncInternalConversations.rejected, (state, action) => {
        if (state.syncRequestId === action.meta.requestId) {
          state.syncRequestId = undefined
        }
      })
  },
})

export const {
  observeInternalConversationChangeSequence,
  setIsChatOpen,
  initOnlineUsers,
  setOnlineUser,
  setIsChangingOpenedInternalConversation,
  setOpenedInternalConversation,
  addInternalConversation,
  updateInternalConversation,
  upsertInternalConversation,
  removeInternalConversation,
  addMessageToInternalConversation,
  addInternalMessageToOpenedInternalConversationMessages,
  addInternalMessagesToOpenedInternalConversationMessages,
  addLoadedInternalConversation,
} = internalConversationsSlice.actions

export default internalConversationsSlice.reducer

export const {
  selectAll: selectAllInternalConversations,
  selectById: selectInternalConversationById,
  selectIds: selectInternalConversationIds,
} = internalConversationsAdapter.getSelectors(
  (state: RootState) => state.internalConversations
)

export const isConversationSelected = (id: number) =>
  createSelector(
    (state: RootState) => state.conversations.selectedIds,
    (selectedIds) => selectedIds.includes(id)
  )
